import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const TOKEN_KEY = 'rms_token';
const REFRESH_TOKEN_KEY = 'rms_refresh_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up axios defaults and interceptors
  useEffect(() => {
    const isProduction = process.env.NODE_ENV === 'production';
    axios.defaults.baseURL = process.env.REACT_APP_API_URL || (isProduction ? '' : 'http://localhost:3001');

    // Set auth header from stored token
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Response interceptor for automatic token refresh
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 (unauthorized) and we haven't already tried to refresh.
        // Skip the refresh endpoint itself, otherwise a dead refresh token would
        // make this handler recurse on its own 401 instead of redirecting to login.
        // Note: 403 (forbidden) is an authorization error, not authentication - don't refresh
        const isRefreshCall = originalRequest.url?.includes('/api/auth/refresh');
        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshCall) {
          originalRequest._retry = true;

          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (refreshToken) {
            try {
              const response = await axios.post('/api/auth/refresh', { refreshToken });
              const { token: newToken } = response.data;

              if (newToken) {
                localStorage.setItem(TOKEN_KEY, newToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return axios(originalRequest);
              }
            } catch (refreshError) {
              // Refresh failed - clear tokens and redirect
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
              delete axios.defaults.headers.common['Authorization'];
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check if user has RMS Admin role
  const checkIsAdmin = useCallback((userData) => {
    if (!userData?.apps) return false;
    const rmsApp = userData.apps.find(app => app.app_name === 'RMS');
    return rmsApp?.role_name === 'Admin';
  }, []);

  // Validate token and load user profile on mount
  const checkAuthStatus = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get('/api/auth/profile');

      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        // Invalid response - clear tokens
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        delete axios.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Token invalid or expired - clear tokens
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });

      if (response.data?.token && response.data?.user) {
        const { token, refreshToken, user: userData } = response.data;

        // Store tokens
        localStorage.setItem(TOKEN_KEY, token);
        if (refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }

        // Set auth header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Set user state
        setUser(userData);

        return { success: true };
      }

      return { success: false, message: 'Invalid response from server' };
    } catch (error) {
      console.error('Login error:', error);

      // Handle rate limiting (429)
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter;
        const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 15;
        return {
          success: false,
          message: `Too many login attempts. Please wait ${minutes} minute(s) and try again.`,
          rateLimited: true
        };
      }

      const message = error.response?.data?.error || 'Login failed. Please try again.';
      return { success: false, message };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    window.location.href = '/login';
  }, []);

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);
      if (response.data?.message) {
        // Refresh user data
        await checkAuthStatus();
        return { success: true };
      }
      return { success: false, message: 'Update failed' };
    } catch (error) {
      console.error('Profile update error:', error);
      const message = error.response?.data?.error || 'Failed to update profile';
      return { success: false, message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.put('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      if (response.data?.message) {
        return { success: true };
      }
      return { success: false, message: 'Password change failed' };
    } catch (error) {
      console.error('Password change error:', error);
      const message = error.response?.data?.error || 'Failed to change password';
      return { success: false, message };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: checkIsAdmin(user)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
